import type { Booking, BookingGarment, BookingWearer, PrintLetterhead } from '../api/bookings';
import { measureGroups, measureLabels } from '../bookings/alterations';
import { referenceLabel } from '../bookings/reference';
import { isOffTheBooking } from '../bookings/tones';
import { withReference } from '../units/referenceNumber';
import { alterationFigure } from './alterationNarrative';
import { escapeHtml, mmToPt, type PrintPage } from './printHtml';

/**
 * The shop's own alteration card, printed (owner, 2026-10-17, from a photograph of the paper card):
 * the shop's name, the booking's number in the box at the top right, નામ, the two columns of boxes
 * — upper લં. સો. છા. પે. સી. બા. કો., lower લં. ક. સી. જા. જો. મો. ગો. — with the changes from the
 * fitting written in, and વિગત for the garment and the notes. One card per garment.
 *
 * <p>
 * <strong>Printable straight after the fitting</strong>, before anybody has decided which tailor
 * does it — unlike the docket (`docketHtml.ts`), which names the tailor and the due date and speaks
 * in sentences. This is the card the shop has always filled in by hand, so it reads in the shop's
 * short forms and the figures as signed inches, never a sentence.
 * </p>
 */

export interface FittingCardInput {
  booking: Booking;
  letterhead: PrintLetterhead;
  /** One person's cards; absent prints every garment on the booking with changes noted. */
  wearerId?: number | undefined;
}

/** A garment with changes on it, with the person wearing it. */
export interface FittingCardLine {
  garment: BookingGarment;
  wearer: BookingWearer;
}

const e = escapeHtml;

/** The garments this print has a card for — those still on the booking with changes noted. */
export function fittingCards(
  input: Pick<FittingCardInput, 'booking' | 'wearerId'>,
): FittingCardLine[] {
  return input.booking.wearers
    .filter((wearer) => input.wearerId === undefined || wearer.id === input.wearerId)
    .flatMap((wearer) =>
      wearer.garments
        .filter((garment) => !isOffTheBooking(garment.state) && garment.alterations.length > 0)
        .map((garment) => ({ garment, wearer })),
    );
}

export function fittingCardPage(input: FittingCardInput): PrintPage {
  const cards = fittingCards(input);
  const width = 148;
  const height = 210;
  const margin = 10;

  const css = `
    @page { size: A5; margin: ${margin}mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { color: #1f2a5c; font-family: 'Noto Sans Gujarati', 'Noto Sans', Roboto, Arial, sans-serif;
           font-size: 12pt; }
    .card { break-after: page; page-break-after: always; border: 1pt solid #1f2a5c; border-radius: 4pt;
            padding: 10pt 12pt; }
    .card:last-child { break-after: auto; page-break-after: auto; }
    .shop { text-align: center; font-weight: 700; font-size: 15pt; letter-spacing: 0.5pt; }
    .local { text-align: center; font-size: 10pt; color: #4b5583; }
    .top { display: flex; justify-content: flex-end; margin-top: 8pt; }
    .number { border: 1.25pt solid #1f2a5c; min-width: 42mm; padding: 4pt 8pt; text-align: center;
              font-weight: 700; font-size: 12pt; }
    .name { display: flex; gap: 6pt; align-items: baseline; border-bottom: 1.25pt solid #1f2a5c;
            padding-bottom: 3pt; margin-top: 2pt; font-weight: 700; }
    .name span:last-child { font-weight: 600; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; }
    .col { padding: 6pt 0; }
    .col + .col { border-left: 1.25pt solid #1f2a5c; padding-left: 12pt; }
    .row { display: flex; align-items: baseline; gap: 10pt; min-height: 22pt; }
    .key { width: 14mm; font-weight: 700; }
    .val { font-weight: 700; font-size: 13pt; color: #111; }
    .details { border-top: 1.25pt solid #1f2a5c; padding-top: 6pt; }
    .details b { margin-right: 6pt; }
    .details p { margin: 3pt 0 0; color: #111; font-size: 11pt; }
    .code { font-family: Menlo, 'Courier New', monospace; font-size: 10pt; }
    @media screen { body { width: ${width}mm; padding: ${margin}mm; } }`;

  const body = cards.map((line) => card(input, line)).join('');
  const viewportPx = Math.round((width / 25.4) * 96);

  return {
    html: `<!doctype html><html lang="gu"><head><meta charset="utf-8"><meta name="viewport" content="width=${viewportPx}"><title>Alteration card</title><style>${css}</style></head><body>${body}</body></html>`,
    widthPt: mmToPt(width),
    heightPt: mmToPt(height),
  };
}

function card(input: FittingCardInput, { garment, wearer }: FittingCardLine): string {
  const byMeasure = new Map(garment.alterations.map((row) => [row.measure, row]));
  const onCard = new Set(measureGroups.flatMap((group) => group.measures));

  const column = (measures: readonly string[]) =>
    measures
      .map((measure) => {
        const row = byMeasure.get(measure);

        return `<div class="row"><span class="key">${e(measureLabels[measure]?.short ?? measure)}</span><span class="val">${row ? e(alterationFigure(row)) : ''}</span></div>`;
      })
      .join('');

  // વિગત: the garment, then what the boxes cannot hold — a note against a measure, and any measure
  // the card has no box for, written out with its figure.
  const notes = garment.alterations
    .filter((row) => row.note || !onCard.has(row.measure))
    .map((row) => {
      const label = measureLabels[row.measure]?.gujarati ?? row.measure;
      const figure = onCard.has(row.measure) ? '' : ` ${alterationFigure(row)}`;

      return `<p>${e(label)}${e(figure)}${row.note ? ` — ${e(row.note)}` : ''}</p>`;
    })
    .join('');

  const [upper, lower] = measureGroups;
  const who = wearer.relation ? `${wearer.name} · ${wearer.relation}` : wearer.name;

  return `<section class="card">
    <div class="shop">${e(input.letterhead.tenantName)}</div>
    ${input.letterhead.tenantNameLocal ? `<div class="local">${e(input.letterhead.tenantNameLocal)}</div>` : ''}
    <div class="top"><div class="number">${e(referenceLabel(input.booking))}</div></div>
    <div class="name"><span>નામ :</span><span>${e(who)}</span></div>
    <div class="grid">
      <div class="col">${column(upper?.measures ?? [])}</div>
      <div class="col">${column(lower?.measures ?? [])}</div>
    </div>
    <div class="details"><b>વિગત :</b>${e(garment.itemName)} · <span class="code">${e(withReference(garment.serialCode, garment.referenceNumber))}</span>${notes}</div>
  </section>`;
}
